/**
 * @astrasyncai/adapter-cursor
 *
 * Layer 4 adapter for Cursor / VS Code AI agents.
 * Intercepts file operations, terminal commands, and network requests
 * made by the AI agent within the editor.
 *
 * The adapter does NOT depend on @types/vscode. Instead, the VS Code
 * extension passes the real `vscode` module via a minimal interface
 * at initialize() time. This keeps the library free of extension host deps.
 *
 * ~250 lines — thin, disposable, platform-specific.
 */

import type { PlatformAdapter, AdapterConfig } from '../adapter-interface/interface';
import type { PDLSSContext, VerificationDecision, AgentAction, InterceptResult } from '../gateway/types';
import type { AstraSyncGateway } from '../gateway/gateway';
import { ADAPTER_INTERFACE_VERSION } from '../adapter-interface/interface';
import { mapToolToPurpose, extractTarget, extractNetworkDomains } from '../adapter-interface/purpose-mapping';

// -----------------------------------------------------------------------
// Minimal VS Code type stubs (injected at runtime by the extension)
// -----------------------------------------------------------------------

export interface Disposable {
  dispose(): void;
}

export interface VSCodeAPI {
  workspace: {
    onDidCreateFiles: (listener: (e: FileEvent) => void) => Disposable;
    onDidDeleteFiles: (listener: (e: FileEvent) => void) => Disposable;
    onDidSaveTextDocument: (listener: (doc: TextDocumentLike) => void) => Disposable;
  };
  window: {
    onDidOpenTerminal: (listener: (terminal: TerminalLike) => void) => Disposable;
    showWarningMessage(message: string, ...items: string[]): Thenable<string | undefined>;
    showInformationMessage(message: string, ...items: string[]): Thenable<string | undefined>;
    showErrorMessage(message: string, ...items: string[]): Thenable<string | undefined>;
    createOutputChannel(name: string): OutputChannelLike;
  };
  commands: {
    registerCommand(command: string, callback: (...args: unknown[]) => unknown): Disposable;
  };
}

interface FileEvent {
  files: ReadonlyArray<{ fsPath: string; path: string }>;
}

interface TextDocumentLike {
  uri: { fsPath: string; path: string };
  languageId: string;
  fileName: string;
}

interface TerminalLike {
  name: string;
  processId: Thenable<number | undefined>;
  sendText(text: string, addNewLine?: boolean): void;
}

type Thenable<T> = PromiseLike<T>;

interface OutputChannelLike {
  appendLine(value: string): void;
  show(): void;
  dispose(): void;
}

// -----------------------------------------------------------------------
// Configuration
// -----------------------------------------------------------------------

export interface CursorAdapterOptions {
  /** The VS Code API object (passed by the extension at runtime) */
  vscode: VSCodeAPI;
  /** Callback for MANUAL_REVIEW decisions */
  onApprovalRequired?: (context: PDLSSContext, decision: VerificationDecision) => Promise<boolean>;
}

// -----------------------------------------------------------------------
// Adapter implementation
// -----------------------------------------------------------------------

export class CursorAdapter implements PlatformAdapter {
  readonly interfaceVersion = ADAPTER_INTERFACE_VERSION;

  private gateway!: AstraSyncGateway;
  private vscode!: VSCodeAPI;
  private disposables: Disposable[] = [];
  private outputChannel: OutputChannelLike | null = null;
  private onApprovalRequired: (context: PDLSSContext, decision: VerificationDecision) => Promise<boolean>;
  private _isRunning = false;

  constructor(options?: Partial<CursorAdapterOptions>) {
    this.onApprovalRequired = options?.onApprovalRequired ?? (async () => false);
    if (options?.vscode) {
      this.vscode = options.vscode;
    }
  }

  get isRunning(): boolean {
    return this._isRunning;
  }

  async initialize(config: AdapterConfig): Promise<void> {
    this.gateway = config.gateway as AstraSyncGateway;

    if (config.adapterOptions.vscode) {
      this.vscode = config.adapterOptions.vscode as VSCodeAPI;
    }

    if (!this.vscode) {
      throw new Error('CursorAdapter requires vscode API — pass it via adapterOptions.vscode');
    }

    // Output channel for logging decisions
    this.outputChannel = this.vscode.window.createOutputChannel('AstraSync Local Guard');

    // Register file watchers
    this.disposables.push(
      this.vscode.workspace.onDidCreateFiles((e) => {
        for (const file of e.files) {
          this.evaluateFileAction('file_write', file.fsPath);
        }
      }),
    );

    this.disposables.push(
      this.vscode.workspace.onDidDeleteFiles((e) => {
        for (const file of e.files) {
          this.evaluateFileAction('file_delete', file.fsPath);
        }
      }),
    );

    this.disposables.push(
      this.vscode.workspace.onDidSaveTextDocument((doc) => {
        this.evaluateFileAction('file_write', doc.fileName);
      }),
    );

    // Register status command
    this.disposables.push(
      this.vscode.commands.registerCommand('astrasync.status', () => {
        this.outputChannel?.show();
        this.vscode.window.showInformationMessage('AstraSync Local Guard is active');
      }),
    );

    this._isRunning = true;
    this.log('AstraSync Local Guard for Cursor — initialized');
  }

  async shutdown(): Promise<void> {
    for (const d of this.disposables) {
      d.dispose();
    }
    this.disposables = [];
    this.outputChannel?.dispose();
    this.outputChannel = null;
    this._isRunning = false;
  }

  async interceptAction(action: AgentAction): Promise<InterceptResult> {
    const raw = action.raw as { type: string; path?: string; command?: string };

    if (!raw.type) {
      return { intercepted: false, skipReason: 'No action type' };
    }

    const context = this.extractContext(action);
    return { intercepted: true, context };
  }

  extractContext(action: AgentAction): PDLSSContext {
    const raw = action.raw as { type: string; path?: string; command?: string; url?: string };

    let toolName: string;
    const args: Record<string, unknown> = {};

    switch (raw.type) {
      case 'file.create':
      case 'file.save':
        toolName = 'file_write';
        args.path = raw.path || '';
        break;
      case 'file.delete':
        toolName = 'file_delete';
        args.path = raw.path || '';
        break;
      case 'file.read':
        toolName = 'file_read';
        args.path = raw.path || '';
        break;
      case 'terminal.exec':
        toolName = 'shell_exec';
        args.command = raw.command || '';
        break;
      case 'network.request':
        toolName = 'http_request';
        args.url = raw.url || '';
        break;
      default:
        toolName = raw.type;
    }

    const purpose = mapToolToPurpose(toolName);
    const target = extractTarget(toolName, args);
    const networkAccess = extractNetworkDomains(target);

    return {
      purpose,
      action: toolName,
      target,
      ...(networkAccess && { networkAccess }),
    };
  }

  async enforceDecision(decision: VerificationDecision): Promise<void> {
    switch (decision.recommendation) {
      case 'DENY':
        this.log(`BLOCKED: ${decision.reason}`);
        this.vscode.window.showErrorMessage(
          `AstraSync blocked action: ${decision.reason}`,
        );
        break;
      case 'MANUAL_REVIEW':
        this.log(`REVIEW: ${decision.reason}`);
        break;
      case 'ALLOW':
        // No action needed
        break;
    }
  }

  // =====================================================================
  // Internal helpers
  // =====================================================================

  private async evaluateFileAction(toolName: string, filePath: string): Promise<void> {
    const action: AgentAction = {
      raw: {
        type: toolName === 'file_delete' ? 'file.delete' : 'file.save',
        path: filePath,
      },
      platform: 'cursor',
      timestamp: new Date(),
    };

    const interception = await this.interceptAction(action);
    if (!interception.intercepted || !interception.context) return;

    const decision = await this.gateway.evaluate(interception.context);

    if (decision.recommendation === 'DENY') {
      await this.enforceDecision(decision);
    } else if (decision.recommendation === 'MANUAL_REVIEW') {
      const approved = await this.handleApproval(interception.context, decision);
      if (!approved) {
        await this.enforceDecision({ ...decision, recommendation: 'DENY' });
      }
    }

    this.log(`${decision.recommendation}: ${interception.context.purpose} -> ${interception.context.target}`);
  }

  private async handleApproval(context: PDLSSContext, decision: VerificationDecision): Promise<boolean> {
    if (this.onApprovalRequired) {
      return this.onApprovalRequired(context, decision);
    }

    // Default: VS Code prompt
    const choice = await this.vscode.window.showWarningMessage(
      `AstraSync: ${decision.reason}. Allow?`,
      'Allow',
      'Deny',
    );
    return choice === 'Allow';
  }

  private log(message: string): void {
    this.outputChannel?.appendLine(`[${new Date().toISOString()}] ${message}`);
  }
}
